import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import GatesInfo from "./gates-info";
import { FormInputs, schema } from "./constants";
import { useEffect, useState } from "react";
import LocationsMapLocation from "./google-map-location";
import AddEditServicePopup, {
  ServiceFormInputs,
} from "./add-edit-service-popup";
import ServicesTable from "./services-table";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FormModeTypes, IEnumData } from "src/types/generic";
import { LoadingButton } from "@mui/lab";
import {
  useAddLocationMutation,
  useGetLocationById,
  useUpdateLocationMutation,
} from "src/api/locations";
import { useGetCitiesList } from "src/api/general";
import AddIcon from "@mui/icons-material/Add";
import ActivateCitiesPopup from "./activate-cities-popup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetClientsList } from "src/api/clients";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "src/firebase";
import DeleteIcon from "src/assets/svg-icons/delete.svg";
import AddingIcon from "src/assets/svg-icons/add.svg";

const AddEditLocationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, clientId } = useParams();
  const mode: FormModeTypes = !id ? "add" : "edit";
  let [searchParams] = useSearchParams();
  const clientName = searchParams.get("client");
  const [services, setServices] = useState<ServiceFormInputs[]>([]);
  const [open, setOpen] = useState(false);
  const [openCitiesPopup, setOpenCitiesPopup] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [clickedCoordinates, setClickedCoordinates] = useState<{
    lat: number;
    lng: number;
  }>();
  const [kafdCategories, setKafdCategories] = useState<IEnumData[]>([]);

  const isComingFromActiveLocationsPage = location.pathname.includes("active");

  const breadcrumbs = [
    <Link
      key="1"
      color={"#A1A5B7"}
      underline="hover"
      onClick={() => navigate(-1)}
      component="text"
      sx={{ cursor: "pointer" }}
    >
      {clientId
        ? "Active Clients"
        : isComingFromActiveLocationsPage
          ? "Active Locations"
          : "Archived Locations"}
    </Link>,

    clientId && (
      <Link
        color={"#A1A5B7"}
        component={RouterLink}
        underline="hover"
        to={`/clients/active/${clientId}/locationList`}
        data-testid="breadcrumb-client-name"
      >
        {clientName}
      </Link>
    ),

    <Typography key="3" color="#A1A5B7" data-testid="breadcrumb-current-page">
      {mode === "add" ? "Add New Location" : "Edit Location"}
    </Typography>,
  ];

  const methods = useForm<FormInputs>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
      shippingAddress: "",
      shippingCountry: "Saudi Arabia",
      gates: [
        {
          name: "",
        },
      ],
      clientId: "",
      isKafdReport: false,
      isKafdPreBooking: true,
    },
  });
  const { handleSubmit, formState, reset, control, watch, setValue } = methods;

  const { fields, append, remove } = useFieldArray({
    name: "parkingParcels",
    control,
  });

  const isKafdClient =
    watch("clientId") === process.env.REACT_APP_KAFD_CLIENT_ID;

  const addParcel = () => {
    append({
      name: "",
    });
  };

  const deleteParcel = (index: number) => {
    if (fields.length === 1) {
      return;
    }
    remove(index);
  };

  // APIS
  const { mutate, isPending, status, error } = useAddLocationMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateLocationMutation();
  const { data, isLoading } = useGetLocationById({
    id: id ?? "",
    enabled: !!id,
  });
  const { data: citiesData, refetch: refetchCities } = useGetCitiesList({
    type: "active",
  });
  const { data: clientsData, isLoading: isLoadingClients } = useGetClientsList({
    type: "active",
  });

  const isNotEqualSpaces =
    +watch("totalParkingSpaces") !==
      +watch("valetParkingSpaces") + +watch("selfParkingSpaces") &&
    +watch("totalParkingSpaces") >= 0 &&
    +watch("valetParkingSpaces") >= 0 &&
    +watch("selfParkingSpaces") >= 0;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (services.length === 0) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Add services please",
        },
      ]);
      return;
    }
    if (!clickedCoordinates?.lat) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Select location on map please",
        },
      ]);
      return;
    }
    if (isNotEqualSpaces) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            "Valet parking spaces + Self-parking spaces should equal The total parking spaces input.",
        },
      ]);
      return;
    }

    const cityName =
      citiesData?.data.find((city) => city.id === data.cityId)?.nameEn ?? "";
    const dataToSend = {
      nameEn: data.nameEn.trim(),
      nameAr: data.nameAr?.trim() ?? "",
      locationType: data.type,
      locationAddress: data.shippingAddress.trim(),
      shippingAddress: data.shippingAddress.trim(),
      shippingBuildingNumber: data.shippingBuildingNumber.trim(),
      shippingCity: cityName,
      shippingState: data.shippingState.trim(),
      shippingZip: data.shippingZip,
      shippingCountry: data.shippingCountry,
      clientId: data.clientId ?? "",
      ...(isKafdClient && {
        area: data.area,
        category: data.category
          ? data.category === "contractWalkIn"
            ? ["contract", "walkin"]
            : [data.category]
          : [],
        isKafdMonthlyReport: data.isKafdReport,
        isKafdPreBooking: data.isKafdPreBooking,
        dropOffNames: data.dropOffName ? [data.dropOffName] : [""],
        parkingParcels: (data.parkingParcels ?? []).map(
          (parcel) => parcel.name,
        ) as string[],
      }),
      locationGates: data.gates.map((gate) => gate.name.trim()),
      locationLatitude: clickedCoordinates?.lat,
      locationLongitude: clickedCoordinates?.lng,
      cityId: data.cityId,
      cityName: cityName,
      totalSpaces: +data.totalParkingSpaces,
      valetSpaces: +data.valetParkingSpaces,
      selfSpaces: +data.selfParkingSpaces,
      services: services.map((service) => {
        return {
          type: service.type,
          name: service.name,
          paymentConfig: service.payment,
          ...(Number(service.gracePeriod) >= 0 && {
            gracePeriod: Number(service.gracePeriod),
          }),
          pricingType: service.pricingType,
          ...(Number(service.startingHours) >= 0 && {
            customStartHrs: Number(service.startingHours),
          }),
          ...(Number(service.priceOfExceedingHours) >= 0 && {
            customExceedingPrice: Number(service.priceOfExceedingHours),
          }),
          customExceedingPriceType: service.exceedingPricingType,
          ...(Number(service.ticketViolationPrice) >= 0 && {
            lossViolationPrice: Number(service.ticketViolationPrice),
          }),
          ...(Number(service.price) >= 0 &&
            service.pricingType === "fixed" && {
              fixedPrice: Number(service.price),
            }),
          ...(Number(service.price) >= 0 &&
            service.pricingType === "hourly" && {
              hourlyPrice: Number(service.price),
            }),
          ...(Number(service.price) >= 0 &&
            service.pricingType === "daily" && {
              dailyPrice: Number(service.price),
            }),
          ...(Number(service.price) >= 0 &&
            service.pricingType === "custom" && {
              customPrice: Number(service.price),
            }),
          // customModel: service.pricing,
        };
      }),
      carPreparationTime: data.times,
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({ id: id, ...dataToSend });
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleOpenCitiesPopup = () => {
    setOpenCitiesPopup(true);
  };

  const onCloseCitiesPopup = () => {
    setOpenCitiesPopup(false);
  };

  // Fetch kafd categories enum
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchData = async () => {
      try {
        const docRef = doc(db, "enums", "locationCategories");

        unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const docData = docSnap.data();
            const sortedCategories = [...(docData.data || [])].sort(
              (a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0),
            );
            // insert Contract, Walk-in
            const contractWalkingItem = {
              key: "contractWalkIn",
              label: {
                en: "Contract, Walk-in",
                ar: "Contract, Walk-in",
              },
              sortIndex: 8,
            };
            sortedCategories.splice(2, 0, contractWalkingItem);
            setKafdCategories(sortedCategories);
          } else {
            console.warn("No such enum document found!");
          }
        });
      } catch (error) {
        console.error("Error fetching enum:", error);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (clientId) {
      setValue("clientId", clientId);
    }
  }, [clientId]);

  useEffect(() => {
    if (
      (mode === "add" || (mode === "edit" && fields.length === 0)) &&
      isKafdClient
    ) {
      setValue("parkingParcels", [{ name: "" }]);
    }
  }, [watch("clientId")]);

  //Reset form with Location data to edit
  useEffect(() => {
    if (data && clientsData) {
      reset({
        mode: "edit",
        type: data.locationType,
        clientId: data.clientId || "",
        area: data.area ?? undefined,
        category:
          data.category && data.category.length > 1
            ? "contractWalkIn"
            : (data.category?.[0] ?? ""),
        dropOffName: data.dropOffNames?.[0] ?? "",
        // parkingParcels: data.parkingParcels ?? [],
        parkingParcels:
          data.parkingParcels && data.parkingParcels?.length > 0
            ? data.parkingParcels.map((parcel) => {
                return {
                  name: parcel,
                };
              })
            : [
                {
                  name: "",
                },
              ],

        isKafdReport: !!data.isKafdMonthlyReport,
        isKafdPreBooking: !!data.isKafdPreBooking,
        cityId: data.cityId,
        nameAr: data.nameAr,
        nameEn: data.nameEn,
        shippingAddress: data.shippingAddress,
        shippingBuildingNumber: data.shippingBuildingNumber,
        shippingState: data.shippingState,
        shippingZip: data.shippingZip,
        shippingCountry: data.shippingCountry,
        totalParkingSpaces: data.totalSpaces,
        valetParkingSpaces: data.valetSpaces,
        selfParkingSpaces: data.selfSpaces,
        gates:
          data.locationGates?.length > 0
            ? data.locationGates.map((gate) => {
                return {
                  name: gate,
                };
              })
            : [
                {
                  name: "",
                },
              ],
        times: data.carPreparationTime,
      });
      setClickedCoordinates({
        lat: data.locationLatitude,
        lng: data.locationLongitude,
      });
      setServices(
        data.services.map((service) => {
          return {
            type: service.type,
            name: service.name,
            payment: service.paymentConfig,
            pricingType: service?.pricingType,
            price:
              service.pricingType === "fixed"
                ? service?.fixedPrice
                : service.pricingType === "custom"
                  ? service.customPrice
                  : service.pricingType === "hourly"
                    ? service.hourlyPrice
                    : service.pricingType === "daily"
                      ? service.dailyPrice
                      : undefined,
            gracePeriod:
              service.gracePeriod != null && service.gracePeriod >= 0
                ? service.gracePeriod
                : undefined,
            startingHours: service?.customStartHrs
              ? service?.customStartHrs
              : undefined,
            priceOfExceedingHours: service?.customExceedingPrice
              ? service?.customExceedingPrice
              : undefined,
            pricingModal: "customByHours",
            exceedingPricingType: service?.customExceedingPriceType ?? "hourly",
            ticketViolationPrice: service?.lossViolationPrice
              ? service?.lossViolationPrice
              : undefined,
          };
        }),
      );
    }
  }, [data, clientsData]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The location has been added successfully!",
        },
      ]);
      navigate(
        clientId
          ? `/clients/active/${clientId}/locationList`
          : "/locations/active",
      );
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The location has been updated successfully!",
        },
      ]);
      navigate(-1);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data?.errors?.[0]?.message ??
            errorUpdate?.data?.message ??
            "Oops! Something went wrong.",
        },
      ]);
    }
  }, [statusUpdate]);

  console.log(formState.errors, kafdCategories, errorUpdate);

  if (isLoading || isLoadingClients) {
    return (
      <Box
        height={"800px"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={"30px"} data-testid="add-edit-location-page">
      <Typography
        mb={1}
        fontSize={"22px"}
        fontWeight={600}
        data-testid="location-form-title"
      >
        {mode === "add" ? "Add New Location" : "Edit Location"}
      </Typography>

      {/* <RouterBreadcrumbs /> */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ color: "#5E6278", mb: 3 }}
      >
        {breadcrumbs}
      </Breadcrumbs>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Box
            p={3}
            borderRadius={"10px"}
            border={"1.036px solid #5B5B5B"}
            mb={"33px"}
          >
            <Typography fontSize={"20px"} fontWeight={600} mb={2}>
              Location Info
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="nameEn"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Location Name in English"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.nameEn}
                      helperText={formState.errors.nameEn?.message}
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      data-testid="location-form-nameEn"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="nameAr"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Location Name in Arabic"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.nameAr}
                      helperText={formState.errors.nameAr?.message}
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      data-testid="location-form-nameAr"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formState.errors.type}
                >
                  <InputLabel id="demo-simple-select-label">
                    Location Type
                  </InputLabel>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        label="Location Type"
                        data-testid="location-form-type"
                      >
                        <MenuItem
                          value={"regular"}
                          onClick={() => setValue("category", "")}
                        >
                          Regular
                        </MenuItem>
                        <MenuItem
                          value={"event"}
                          onClick={() => setValue("category", "event")}
                        >
                          Event
                        </MenuItem>
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {formState.errors.type?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formState.errors.clientId}
                >
                  <InputLabel id="demo-simple-select-label">Client</InputLabel>
                  <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Client"
                        disabled={!!clientId || mode === "edit"}
                        data-testid="location-form-clientId"
                      >
                        {clientsData?.data.map((client) => {
                          return (
                            <MenuItem key={client.id} value={client.id}>
                              {client.clientInfo.nameEn}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {formState.errors.clientId?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {isKafdClient && (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!formState.errors.area}
                    >
                      <InputLabel id="demo-simple-select-label">
                        Area
                      </InputLabel>
                      <Controller
                        name="area"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Area"
                            data-testid="location-form-area"
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {formState.errors.area?.message}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!formState.errors.category}
                    >
                      <InputLabel id="demo-simple-select-label">
                        Category
                      </InputLabel>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Category"
                            data-testid="location-form-category"
                          >
                            {kafdCategories?.map((cat) => {
                              return (
                                <MenuItem
                                  disabled={
                                    (watch("type") === "event" &&
                                      cat.key !== "event") ||
                                    (watch("type") === "regular" &&
                                      cat.key === "event")
                                  }
                                  key={cat.key}
                                  value={cat.key}
                                >
                                  {cat.label.en}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {formState.errors.category?.message}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="dropOffName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Drop-off Name"
                          fullWidth
                          size="small"
                          {...field}
                          error={!!formState.errors.dropOffName}
                          helperText={formState.errors.dropOffName?.message}
                          InputLabelProps={{
                            shrink: field.value !== undefined,
                          }}
                          data-testid="location-form-dropOffName"
                        />
                      )}
                    />
                  </Grid>
                  {fields.map((field, index) => (
                    <Grid item xs={12} sm={4} key={field.id}>
                      <Stack
                        direction={"row"}
                        spacing={1}
                        alignItems={"flex-start"}
                      >
                        <Controller
                          name={`parkingParcels.${index}.name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              fullWidth
                              label="Parking Parcel"
                              size="small"
                              {...field}
                              error={
                                !!formState?.errors.parkingParcels?.[index]
                                  ?.name
                              }
                              helperText={
                                formState.errors.parkingParcels?.[index]?.name
                                  ?.message
                              }
                            />
                          )}
                        />

                        {fields.length > 1 && (
                          <IconButton onClick={() => deleteParcel(index)}>
                            <img src={DeleteIcon} alt="" />
                          </IconButton>
                        )}

                        {fields.length - 1 === index && (
                          <IconButton onClick={addParcel}>
                            <img src={AddingIcon} alt="" />
                          </IconButton>
                        )}
                      </Stack>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
            {isKafdClient && (
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} lg={4}>
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Box
                      bgcolor={"primary.main"}
                      borderRadius={"12px"}
                      height={"30px"}
                      width={"3px"}
                    />
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <Controller
                        name="isKafdReport"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ width: "fit-content" }}
                            control={
                              <Checkbox
                                {...field}
                                color="primary"
                                data-testid="location-form-isKafdReport"
                              />
                            }
                            checked={field.value}
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                Add This Location To KAFD Monthly Sales Report.
                              </Typography>
                            }
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} lg={8}>
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Box
                      bgcolor={"primary.main"}
                      borderRadius={"12px"}
                      height={"30px"}
                      width={"3px"}
                    />
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <Controller
                        name="isKafdPreBooking"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ width: "fit-content" }}
                            control={
                              <Checkbox
                                {...field}
                                color="primary"
                                data-testid="location-form-isKafdPreBooking"
                              />
                            }
                            checked={field.value}
                            label={
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                Add This Location To KAFD Pre-booking list.
                              </Typography>
                            }
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Box>
          <Box
            p={3}
            borderRadius={"10px"}
            border={"1.036px solid #5B5B5B"}
            mb={"33px"}
          >
            <Typography fontSize={"20px"} fontWeight={600} mb={2}>
              Location Address
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="shippingAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Street Name"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.shippingAddress}
                      helperText={formState.errors.shippingAddress?.message}
                      InputLabelProps={{
                        shrink: !!field.value,
                      }}
                      data-testid={"location-form-locationAddress"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="shippingBuildingNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Building Number"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.shippingBuildingNumber}
                      helperText={
                        formState.errors.shippingBuildingNumber?.message
                      }
                      InputLabelProps={{
                        shrink: !!field.value,
                      }}
                      data-testid={"location-form-locationBuildingNum"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!formState.errors.cityId}
                >
                  <InputLabel id="demo-simple-select-label">
                    Select a City
                  </InputLabel>
                  <Controller
                    name="cityId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Select a City"
                        data-testid="location-form-city"
                      >
                        {citiesData?.data.map((city) => {
                          return (
                            <MenuItem key={city.id} value={city.id}>
                              {city.nameEn}
                            </MenuItem>
                          );
                        })}
                        <Box p={"4px 8px"}>
                          <Button
                            onClick={handleOpenCitiesPopup}
                            startIcon={<AddIcon />}
                            variant="text"
                          >
                            Activate New City
                          </Button>
                        </Box>
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {formState.errors.cityId?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="shippingState"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="District"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.shippingState}
                      helperText={formState.errors.shippingState?.message}
                      InputLabelProps={{
                        shrink: !!field.value,
                      }}
                      data-testid={"location-form-state"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="shippingZip"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Postal Code"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.shippingZip}
                      helperText={formState.errors.shippingZip?.message}
                      InputLabelProps={{
                        shrink: !!field.value,
                      }}
                      data-testid={"location-form-zip"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="shippingCountry"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Country"
                      fullWidth
                      size="small"
                      {...field}
                      error={!!formState.errors.shippingCountry}
                      helperText={formState.errors.shippingCountry?.message}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      data-testid={"location-form-country"}
                      disabled
                    />
                  )}
                />
              </Grid>
            </Grid>
            {/* google map location */}
            <Box mt={2}>
              <LocationsMapLocation
                setClickedCoordinates={setClickedCoordinates}
                clickedCoordinates={clickedCoordinates}
              />
            </Box>
          </Box>

          {/* gates */}
          <GatesInfo isNotEqualSpaces={isNotEqualSpaces} />
          {/* services */}
          <Box
            p={3}
            borderRadius={"10px"}
            border={"1.036px solid #5B5B5B"}
            mb={"33px"}
          >
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography fontSize={"20px"} fontWeight={600}>
                Services
              </Typography>
              <Button
                variant="contained"
                onClick={handleOpen}
                data-testid="location-form-addServiceBtn"
              >
                Add New Service
              </Button>
            </Stack>
            {services.length === 0 ? (
              <Box py={"80px"}>
                <Typography
                  fontSize={"18px"}
                  color={"#A3A3A3"}
                  fontWeight={500}
                  align="center"
                  data-testid="location-form-no-services"
                >
                  No Services Added Yet
                </Typography>
              </Box>
            ) : (
              <Box mt={"40px"}>
                <ServicesTable
                  services={services}
                  setServices={setServices}
                  locationId={id}
                />
              </Box>
            )}
          </Box>
          <Box display={"flex"} justifyContent={"flex-end"}>
            <Stack mt={3} spacing={2} direction={"row"}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ minWidth: { xs: "100px", md: "208px" } }}
                data-testid="location-form-cancelBtn"
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={isPending || isPendingUpdate}
                sx={{ minWidth: { xs: "100px", md: "208px" } }}
                data-testid="location-form-submitBtn"
              >
                {mode === "add" ? "Add Location" : "Update Location"}
              </LoadingButton>
            </Stack>
          </Box>
        </FormProvider>
      </form>
      {open && (
        <AddEditServicePopup
          open={open}
          onClose={onClose}
          locationId={id}
          setServices={setServices}
          services={services}
        />
      )}
      {openCitiesPopup && (
        <ActivateCitiesPopup
          open={openCitiesPopup}
          onClose={onCloseCitiesPopup}
          refetchCities={refetchCities}
        />
      )}
    </Box>
  );
};

export default AddEditLocationPage;
